import { useCallback, Dispatch } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { VcsProvider } from "types/generated";
import useQueryAttachableVCSIntegrationsOptions from "components/AttachableVCSIntegrationsOptions/useQueryAttachableVCSIntegrationsOptions";
import { SelectedVcsIntegration } from "components/AttachableVCSIntegrationsOptions/types";
import ComboBox from "ds/components/ComboBox";

import VcsIntegrationFieldTooltip from "./Tooltip";
import { getTooltipAnalyticsProps } from "../utils";
import { InternalFormData, SourceCodeSettingsField, SourceCodeProjects } from "../types";
import SourceCodeIntegrationFieldComboBoxItem from "./ComboBoxItem";

type SourceCodeIntegrationFieldProps = {
  provider: VcsProvider;
  spaceId: string;
  setInternalFormData?: Dispatch<React.SetStateAction<InternalFormData>>;
  analyticsVersion?: string;
  projectType: SourceCodeProjects;
};

const SourceCodeIntegrationField = ({
  provider,
  spaceId,
  setInternalFormData,
  analyticsVersion,
  projectType,
}: SourceCodeIntegrationFieldProps) => {
  const { control, setValue, watch } = useFormContext<SourceCodeSettingsField>();

  const formValues = watch();

  const handleSetInitialValue = useCallback(
    (integration: SelectedVcsIntegration) => {
      setValue("vcsIntegrationId", integration.id, { shouldValidate: true, shouldDirty: true });
      if (!integration.useGitCheckout) {
        setValue("gitSparseCheckoutPaths", []);
      }

      setInternalFormData?.((state) => ({
        ...state,
        vcsIntegrationLabel: integration.name,
        useGitCheckout: integration.useGitCheckout,
      }));
    },
    [setInternalFormData, setValue]
  );

  const { options, loading } = useQueryAttachableVCSIntegrationsOptions({
    provider,
    spaceId,
    onSelect: handleSetInitialValue,
    initialValue: formValues.vcsIntegrationId,
  });

  const handleChange = useCallback(
    (onChange: (key: string | null) => void) => (value: string | null) => {
      onChange(value);

      const option = options.find((option) => option.value === value);
      if (value && option) {
        setInternalFormData?.((state) => ({
          ...state,
          vcsIntegrationLabel: option.label,
          useGitCheckout: "useGitCheckout" in option ? option.useGitCheckout : undefined,
        }));
      }

      setValue("repository", "", { shouldDirty: true });
      setValue("branch", "", { shouldDirty: true });
      setValue("gitSparseCheckoutPaths", []);
    },
    [setInternalFormData, setValue, options]
  );

  return (
    <Controller
      name="vcsIntegrationId"
      control={control}
      rules={{ required: "Integration is required" }}
      render={({ field, fieldState }) => (
        <ComboBox
          label="Integration"
          {...getTooltipAnalyticsProps("Source Code", "Integration", projectType, {
            provider: formValues.vcsIntegrationId,
            version: analyticsVersion,
          })}
          tooltipInfo={<VcsIntegrationFieldTooltip provider={provider} projectType={projectType} />}
          error={fieldState.error?.message}
          value={field.value}
          items={options}
          onChange={handleChange(field.onChange)}
          isLoading={loading}
        >
          {(item) => <SourceCodeIntegrationFieldComboBoxItem {...item} id={item.value} />}
        </ComboBox>
      )}
    />
  );
};

export default SourceCodeIntegrationField;
